import styled from 'styled-components';
import {Icon} from '@forecast-it/design-system';

/**
 * What kind of thing happened. Drives the timeline glyph, so a long history can be
 * skimmed by shape before any of it is read.
 */
export type ActivityKind = 'emailIn' | 'emailOut' | 'note' | 'status' | 'assign' | 'attachment' | 'created' | 'other';

/**
 * The glyph per kind. Every value is a real design-system icon name; there is no
 * fallback beyond `other`, so a new kind must choose its own glyph rather than
 * silently inheriting one.
 */
const KIND_ICONS: Record<ActivityKind, string> = {
	emailIn: 'mail',
	emailOut: 'mail',
	note: 'feedback',
	status: 'inProgress',
	assign: 'profile',
	attachment: 'paperclip',
	created: 'plus',
	other: 'info',
};

/** Which `EventActivity.eventType` maps to which kind. */
export function kindForEventType(eventType: string): ActivityKind {
	if (eventType === 'status_change') return 'status';
	if (eventType === 'assignment') return 'assign';
	if (eventType === 'created') return 'created';
	// FOR-261: a deleted file is still a file event, so it takes the paperclip that
	// "Attachment added" already uses — the entry's own text says which happened.
	if (eventType === 'attachment.deleted') return 'attachment';
	return 'other';
}

// A white disc on the rail, so the connecting line appears to pass behind each
// entry rather than through it. The glyph is the only thing inside — colour is not
// the carrier here, since a timeline of coloured discs reads as decoration.
const Disc = styled.span`
	position: relative;
	z-index: 1;
	display: flex;
	flex: none;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	border-radius: 9999px;
	border: 1px solid ${p => p.theme.sys.color.border.decorative};
	background: ${p => p.theme.sys.color.background.neutral['1']};
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;

/**
 * One entry's marker on the activity rail.
 *
 * The glyph is decorative — the entry's own header says who did what — so it is
 * wrapped in an element carrying the kind instead of an accessible name. That
 * wrapper is also the only way to assert which glyph rendered: the design system's
 * `Icon` emits nothing naming its own shape.
 */
export function ActivityBadge({kind}: {kind: ActivityKind}) {
	return (
		<Disc data-testid="activity-badge" data-activity-kind={kind}>
			<Icon icon={KIND_ICONS[kind]} size="s" />
		</Disc>
	);
}
